import {
  handleResp,
  getURLWithQueryParams,
  getDefaultHeaders,
  extendFetchOptions,
  CashifyFunction,
} from "./utils";

import type {
  FunctionMetadata,
  CommonResponse,
  GenericResponse,
  WrappedCashifyFunction,
} from "./utils";
import type { StandardObject } from "utils";

export interface TenantService {
  findTenant: ReturnType<typeof findTenant>;
}

export interface AdminAuthenticationService {
  authAdminStandard: ReturnType<typeof authAdmin>;
  authAdminEmergency: ReturnType<typeof authAdmin>;
  logoutAdmin: ReturnType<typeof logoutAdmin>;
  changePassword: ReturnType<typeof changePassword>;
}

interface TenantDTO {
  id: number;
  name: string;
  contactEmail?: string;
  createTime?: string;
  createAdmin?: string;
  lastUpdateTime?: string;
  lastUpdateAdmin?: string;
}

export type TokenUserType = "ADMIN" | "PRE_ADMIN";
export type MfaMethods = "EMAIL_OTP" | "TOTP";

interface AuthenticationMetadata extends FunctionMetadata {
  type: "STANDARD" | "EMERGENCY";
}

interface AuthenticationRequest {
  tenantId: number;
  username: string;
  password: string;
}

export interface AuthenticationResponse {
  adminId?: number;
  accessToken: string;
  expiresIn?: number;
  refreshToken?: string;
  refreshExpiresIn?: number;
  tokenType?: string;
  type: TokenUserType;
  mfaMethods: MfaMethods[]; 
  scope?: string;
  passwordExpired?: boolean;
}

type FindTenantResponse = GenericResponse<TenantDTO>;

type AdminAuthenticationResponse = GenericResponse<AuthenticationResponse>;

export const findTenant = ({
  urlPrefix,
  language,
}: FunctionMetadata): CashifyFunction<
  { tenantName: string },
  FindTenantResponse
> => {
  return async ({ tenantName }) => {
    let queryParams = { tenantName };
    let url = getURLWithQueryParams(
      `${urlPrefix}/api/v1/tenant/find`,
      queryParams
    );
    let options = {
      headers: {
        ...getDefaultHeaders(),
        "x-language": String(language ?? "en-US"),
      }
    };
    return fetch(url, options).then((resp) => handleResp(resp));
  };
};

export const authAdmin = ({
  urlPrefix,
  language,
  type,
}: AuthenticationMetadata): CashifyFunction<
  AuthenticationRequest,
  AdminAuthenticationResponse
> => {
  return async ({ tenantId, username, password }) => {
    let url = `${urlPrefix}/api/v1/auth/admin`;
    let opts = {
      method: "POST",
      headers: {
        ...getDefaultHeaders(),
        "Content-Type": "application/json",
        "x-tenant-id": String(tenantId),
        "x-language": String(language ?? "en-US"),
      },
      body: JSON.stringify({ type: type, username, password }),
    };
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};

export const logoutAdmin: WrappedCashifyFunction<{}, CommonResponse> = ({
  urlPrefix,
  options,
}) => {
  return async () => {
    let url = `${urlPrefix}/api/v1/auth/admin/logout`;
    let opts = extendFetchOptions(
      {
        method: "POST",
        headers: getDefaultHeaders(),
      },
      options
    );
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};

interface RenewalRequest {
  tenantId: number;
  username: string;
}

export const refreshAuth = ({ urlPrefix, language }: FunctionMetadata) => {
  return ({ tenantId, username }: RenewalRequest) => {
    const type = "RENEWAL";
    let url = `${urlPrefix}/api/v1/auth/admin`;
    let opts: StandardObject = {
      method: "POST",
      credentials: "same-origin",
      headers: {
        ...getDefaultHeaders(),
        "Content-Type": "application/json",
        "x-tenant-id": tenantId,
        "x-language": language ?? "en-US",
      },
      body: JSON.stringify({ type: type, username }),
    };
    return fetch(url, opts);
  };
};

interface changePasswordRequest {
  id: number;
  originalPassword: string;
  newPassword: string;
}

export const changePassword: WrappedCashifyFunction<
  changePasswordRequest,
  CommonResponse
> = ({ urlPrefix, options }) => {
  return async ({ id, originalPassword, newPassword }) => {
    let url = `${urlPrefix}/api/v1/admin/${id}/password`;
    let opts = extendFetchOptions(
      {
        method: "PUT",
        headers: {
          ...getDefaultHeaders(),
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ originalPassword, newPassword }),
      },
      options
    );
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};
